import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { useGameStore } from '../../core/store/gameStore';

// ============================================================================
// ImportedScene - renders a user-uploaded SketchUp export (.glb / Draco .glb)
// inside the Yosemite valley. Once loaded the model is auto-scaled to a
// walkable size, grounded on the real terrain near the player, and can be
// rotated / rescaled / walked into through the import toolbar in the UI.
//
// Design notes:
//  * The loader pipeline (GLTFLoader + DRACOLoader with /draco/ decoders) is
//    the same one GugongPalace already uses, so compressed exports load too.
//  * Files stay local (blob object URLs) - nothing is uploaded anywhere.
//  * No colliders: the character walks on terrain height only, so ground-level
//    floors of imported architecture read fine while walking through.
// ============================================================================

const PLACE_ANGLES = 16;          // flat-pad scan directions
const PLACE_RADII = [6, 9, 12, 16, 20, 26, 32];
const MAX_VERTICAL = 16;          // clamp model height (after base scale)
const MIN_VERTICAL = 2.2;         // keep tiny models visible

interface PlacedInfo {
  x: number;
  z: number;
  groundY: number;
  halfH: number;    // half vertical extent in model units
  baseScale: number;
}

function scanFlatPad(
  terrain: { heightAt: (x: number, z: number) => number },
  ax: number, az: number,
  halfFoot: number,
): { x: number; z: number; y: number } | null {
  const step = (Math.PI * 2) / PLACE_ANGLES;
  for (const r of PLACE_RADII) {
    for (let i = 0; i < PLACE_ANGLES; i++) {
      const a = i * step;
      const cx = ax + Math.cos(a) * r;
      const cz = az + Math.sin(a) * r;
      const cy = terrain.heightAt(cx, cz);
      if (!Number.isFinite(cy)) continue;
      // Sample the pad footprint corners for flatness.
      const f = Math.max(halfFoot, 1.5);
      const probes = [
        [cx - f, cz - f], [cx + f, cz - f],
        [cx - f, cz + f], [cx + f, cz + f],
      ];
      let ok = true;
      let min = cy, max = cy;
      for (const [px, pz] of probes) {
        const h = terrain.heightAt(px, pz);
        if (!Number.isFinite(h)) { ok = false; break; }
        min = Math.min(min, h);
        max = Math.max(max, h);
      }
      // Flat enough to stand on and no extreme cliff within the pad.
      if (ok && max - min < 0.85 && max - cy < 2.5 && cy - min < 2.5) {
        return { x: cx, z: cz, y: cy };
      }
    }
  }
  return null;
}

export function ImportedScene() {
  const controls = useThree((s: any) => s.controls) as {
    setLookAt?: (x: number, y: number, z: number, tx: number, ty: number, tz: number, transition?: boolean) => unknown;
  } | null;

  const model = useGameStore((s) => s.importedModel);
  const terrain = useGameStore((s) => s.terrain);
  const importRotY = useGameStore((s) => s.importRotY);
  const importScaleMul = useGameStore((s) => s.importScaleMul);
  const importViewTick = useGameStore((s) => s.importViewTick);
  const requestImportView = useGameStore((s) => s.requestImportView);
  const setImportError = useGameStore((s) => s.setImportError);

  const [gltfObject, setGltfObject] = useState<THREE.Object3D | null>(null);
  const [placed, setPlaced] = useState<PlacedInfo | null>(null);
  const groupRef = useRef<THREE.Group>(null);
  const placedRef = useRef<PlacedInfo | null>(null);
  placedRef.current = placed;

  const loader = useMemo(() => {
    const draco = new DRACOLoader();
    draco.setDecoderPath('/draco/');
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(draco);
    return gltfLoader;
  }, []);

  // Load + place a newly selected model.
  useEffect(() => {
    if (!model || !terrain) return;
    let cancelled = false;

    setPlaced(null);
    setGltfObject(null);
    setImportError(null);

    loader.load(
      model.url,
      (gltf) => {
        if (cancelled) return;
        const obj = gltf.scene;
        obj.updateMatrixWorld(true);

        const local = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        local.getSize(size);
        const center = new THREE.Vector3();
        local.getCenter(center);

        if (size.x < 1e-5 || size.y < 1e-5 || size.z < 1e-5) {
          setImportError('模型中没有任何可见网格，无法放置。');
          return;
        }

        // Center the pivot on the model so rotation spins about its middle.
        obj.position.x -= center.x;
        obj.position.y -= center.y;
        obj.position.z -= center.z;

        // Walkable base scale: keep real proportions but clamp extremes.
        const vertical = size.y;
        let baseScale = 1;
        if (vertical < MIN_VERTICAL) baseScale = MIN_VERTICAL / vertical;
        else if (vertical > MAX_VERTICAL) baseScale = MAX_VERTICAL / vertical;

        const halfW = (Math.max(size.x, size.z) / 2) * baseScale;

        // Anchor near the player's current position and find a flat pad.
        const char = useGameStore.getState().characterRef?.current;
        const ax = char ? char.position.x : 0;
        const az = char ? char.position.z : 0;
        const pad = terrain
          ? scanFlatPad(terrain, ax, az, halfW)
          : null;
        const px = pad ? pad.x : ax;
        const pz = pad ? pad.z : az;
        const py = pad ? pad.y : terrain.heightAt(ax, az);

        setGltfObject(obj);
        setPlaced({
          x: px,
          z: pz,
          groundY: Number.isFinite(py) ? py : 0,
          halfH: size.y / 2,
          baseScale,
        });

        // Frame the fresh import for the player once.
        requestAnimationFrame(() => useGameStore.getState().requestImportView());
      },
      undefined,
      (err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setImportError(`模型加载失败：${msg}\n请确认这是从 SketchUp 导出的 .glb 文件。`);
      },
    );

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, terrain]);

  // Apply rotation / scale from the toolbar onto the placed group.
  useEffect(() => {
    const g = groupRef.current;
    if (!g || !placed) return;
    g.rotation.y = importRotY;
    g.scale.setScalar(placed.baseScale * importScaleMul);
    g.position.set(placed.x, placed.groundY + placed.halfH * placed.baseScale * importScaleMul, placed.z);
  }, [placed, importRotY, importScaleMul]);

  // Walk the character to the model and frame it in the camera.
  useEffect(() => {
    const g = groupRef.current;
    if (!g || importViewTick === 0) return;
    g.updateMatrixWorld(true);
    const world = new THREE.Box3().setFromObject(g);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    world.getSize(size);
    world.getCenter(center);

    const hw = Math.max(size.x, size.z) / 2;
    const radius = hw + 3.2;

    // Pick an approach direction that is flat enough to stand on.
    const terrainNow = useGameStore.getState().terrain;
    let dir = new THREE.Vector3(0, 0, 1);
    if (terrainNow) {
      for (let i = 0; i < PLACE_ANGLES; i++) {
        const a = (i / PLACE_ANGLES) * Math.PI * 2;
        const d = new THREE.Vector3(Math.sin(a), 0, Math.cos(a));
        const h1 = terrainNow.heightAt(center.x + d.x * radius, center.z + d.z * radius);
        const h2 = terrainNow.heightAt(center.x + d.x * (radius + 2), center.z + d.z * (radius + 2));
        if (Number.isFinite(h1) && Number.isFinite(h2) && Math.abs(h1 - h2) < 0.8) {
          dir = d;
          break;
        }
      }
    }

    const char = useGameStore.getState().characterRef?.current;
    if (char) {
      char.position.set(
        center.x - dir.x * radius,
        (terrainNow?.heightAt(center.x - dir.x * radius, center.z - dir.z * radius) ?? center.y) + 1.5,
        center.z - dir.z * radius,
      );
      char.rotation.y = Math.atan2(dir.x, dir.z);
    }

    controls?.setLookAt?.(
      center.x - dir.x * (radius + 6),
      center.y + Math.min(size.y, 8) * 0.8 + 2,
      center.z - dir.z * (radius + 6),
      center.x, center.y + 1, center.z,
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importViewTick]);

  // Dispose geometry + materials of a loaded glTF object graph.
  const disposeObject = useCallback((root: THREE.Object3D) => {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if ((mesh as any).isMesh) {
        mesh.geometry?.dispose?.();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((m: any) => {
          if (!m) return;
          Object.keys(m).forEach((k) => {
            const v = m[k];
            if (v && typeof v.dispose === 'function' && v.isTexture) v.dispose();
          });
          m.dispose?.();
        });
      }
    });
  }, []);

  // When a replaced/new model loads, dispose the previous object.
  const objectRef = useRef<THREE.Object3D | null>(null);
  useEffect(() => {
    const prev = objectRef.current;
    objectRef.current = gltfObject;
    return () => { if (prev) disposeObject(prev); };
  }, [gltfObject, disposeObject]);

  // When the model is cleared through the UI, dispose whatever is left.
  useEffect(() => {
    if (!model && objectRef.current) {
      disposeObject(objectRef.current);
      objectRef.current = null;
      setGltfObject(null);
      setPlaced(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  if (!model || !gltfObject || !placed) return null;

  return (
    <group
      ref={groupRef}
      position={[placed.x, placed.groundY + placed.halfH * placed.baseScale, placed.z]}
    >
      <primitive object={gltfObject} />
    </group>
  );
}
